'use client';

import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';

interface PartNameComboboxProps {
  value: string;
  options: string[];
  onChange: (name: string) => void;
}

export function PartNameCombobox({ value, options, onChange }: PartNameComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState(value);

  React.useEffect(() => {
    setQuery(value);
  }, [value]);

  const trimmedQuery = query.trim();
  const filtered = trimmedQuery
    ? options.filter(name => name.toLowerCase().includes(trimmedQuery.toLowerCase()))
    : options;

  function selectOption(name: string) {
    onChange(name);
    setQuery(name);
    setOpen(false);
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <input
          aria-label="Part name"
          className="w-full rounded border border-gray-200 bg-white px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Select a part…"
        />
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          onOpenAutoFocus={e => e.preventDefault()}
          className="z-50 max-h-64 w-64 min-w-[var(--radix-popover-trigger-width)] overflow-y-auto rounded-md border border-gray-200 bg-white p-1 text-sm shadow-lg"
        >
          {filtered.map(name => (
            <button
              key={name}
              type="button"
              className="block w-full rounded px-2 py-1.5 text-left hover:bg-gray-100"
              onClick={() => selectOption(name)}
            >
              {name}
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="px-2 py-1.5 text-gray-400">
              {trimmedQuery
                ? 'No matching part name — ask a super admin to add it under Lookups'
                : 'No part names available'}
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
